import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Store, ChevronLeft, Loader2, Wallet, Landmark, ArrowUp, ArrowDown, Banknote } from "lucide-react";
import AppShell from "../../components/AppShell";
import ShopLogView, { BalanceStat } from "../../components/mshop/ShopLogView";
import RecordsBrowser from "../../components/mshop/RecordsBrowser";
import { api } from "../../lib/api";

function PageHead({ back, backLabel, title, subtitle }) {
    return (
        <div className="mb-5">
            <Link to={back} className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-600 hover:text-neutral-900" data-testid="mshop-page-back">
                <ArrowRight className="h-4 w-4" /> {backLabel}
            </Link>
            <h1 className="mt-2 font-heading text-3xl font-black text-neutral-900">{title}</h1>
            {subtitle ? <p className="mt-1 text-sm text-neutral-500">{subtitle}</p> : null}
        </div>
    );
}

export function MShopRecordsPage() {
    return (
        <AppShell>
            <div className="mx-auto max-w-xl" dir="rtl">
                <PageHead back="/" backLabel="الرئيسية" title="سجلات الحركة" subtitle="السنة ← الشهر ← اليوم ← العمليات" />
                <RecordsBrowser />
            </div>
        </AppShell>
    );
}

export function MShopLogPage() {
    return (
        <AppShell>
            <div className="mx-auto max-w-xl" dir="rtl">
                <PageHead back="/" backLabel="الرئيسية" title="سجل المحل" subtitle="الذهب المشترى والكاش والبنك" />
                <ShopLogView />
            </div>
        </AppShell>
    );
}

export function MansoriShopsPage() {
    const navigate = useNavigate();
    const [items, setItems] = useState(null);
    const [totals, setTotals] = useState(null);
    useEffect(() => { api.get("/mshop/shops").then((r) => { setItems(r.data.items); setTotals(r.data.totals); }).catch(() => setItems([])); }, []);
    return (
        <AppShell>
            <div className="mx-auto max-w-xl" dir="rtl" data-testid="mansori-shops-page">
                <PageHead back="/" backLabel="الدفاتر" title="سجل المحل" subtitle="كل موظف يمثل محلاً مستقلاً — اختر المحل" />
                {totals && <div className="mb-4 grid grid-cols-2 gap-3">
                    <BalanceStat icon={Wallet} label="الكاش الكلي" value={totals.cash} unit="ر.ع" testId="mansori-total-cash" />
                    <BalanceStat icon={Landmark} label="البنك الكلي" value={totals.bank} unit="ر.ع" testId="mansori-total-bank" />
                </div>}
                {items === null ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-neutral-400" /> : items.length === 0 ? (
                    <div className="rounded-2xl bg-white py-10 text-center text-sm text-neutral-500 ring-1 ring-[#E7E5DF]" data-testid="mansori-shops-empty">لا يوجد موظفون مسجلون بعد</div>
                ) : (
                    <div className="space-y-3">
                        {items.map((s) => (
                            <button key={s.id} onClick={() => navigate(`/mshop/shops/${s.id}`)} className="group flex w-full items-center gap-4 rounded-[24px] bg-white p-5 text-right shadow-sm ring-1 ring-[#E7E5DF] transition-transform hover:-translate-y-0.5" data-testid={`mansori-shop-${s.shop_number}`}>
                                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F6E7C1] text-[#8A6A1F]"><Store className="h-6 w-6" /></span>
                                <span className="flex-1">
                                    <span className="block font-heading text-xl font-bold text-neutral-900">{s.name}</span>
                                    <span className="text-xs text-neutral-500">محل {s.shop_number}{s.opening_set ? "" : " • الرصيد الابتدائي غير محدد"}</span>
                                </span>
                                <ChevronLeft className="h-5 w-5 text-neutral-400 transition-transform group-hover:-translate-x-1" />
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </AppShell>
    );
}

export function MansoriShopDetailPage() {
    const { empId } = useParams();
    const [tab, setTab] = useState("log");
    const [name, setName] = useState("");
    useEffect(() => {
        api.get("/mshop/shops").then((r) => setName(r.data.items.find((x) => x.id === empId)?.name || "")).catch(() => {});
    }, [empId]);
    return (
        <AppShell>
            <div className="mx-auto max-w-xl" dir="rtl" data-testid="mansori-shop-detail">
                <PageHead back="/mshop/shops" backLabel="قائمة المحلات" title={name || "المحل"} />
                <ShopLogView employeeId={empId} />
                <h2 className="mb-3 mt-7 font-heading text-xl font-black text-neutral-900">تفاصيل العمليات</h2>
                <div className="space-y-3">
                    {[["sale", "البيع", ArrowUp], ["purchase", "الشراء", ArrowDown], ["expense", "المصاريف", Banknote]].map(([key, label, Icon]) => (
                        <button key={key} onClick={() => setTab(tab === key ? "log" : key)} className={`flex w-full items-center gap-4 rounded-2xl border p-4 text-right font-bold shadow-sm ${tab === key ? "border-[#8A6A1F] bg-[#F6E7C1]" : "border-[#E7E5DF] bg-white"}`}>
                            <Icon className="h-6 w-6 text-[#8A6A1F]" />
                            <span className="flex-1">{label}</span><ChevronLeft className="h-5 w-5" />
                        </button>
                    ))}
                </div>
                {tab !== "log" && <div className="mt-5"><RecordsBrowser key={`${empId}-${tab}`} employeeId={empId} initialType={tab} hideTabs /></div>}
            </div>
        </AppShell>
    );
}
